import type {
  AtalhoDeSituacao,
  DetalheDaParticipacao,
  FormatoDoArquivo,
  RepresentacaoDeclarada,
  Selo,
  SituacaoDaNegociacaoAssinaturas,
  SituacaoDaParticipacao,
  TipoDaNegociacao,
} from '../types/negociacaoAssinaturasTypes';

/** Fonte única dos nomes das situações, na lista, no detalhe e no painel individual (CA-26). */
export const SELO_DA_SITUACAO: Record<SituacaoDaParticipacao, Selo> = {
  ASSINOU: { rotulo: 'Assinou', variante: 'success' },
  NA_VEZ: { rotulo: 'Na vez', variante: 'brand' },
  AGUARDANDO: { rotulo: 'Aguardando', variante: 'neutral' },
  A_CONFERIR: { rotulo: 'A conferir', variante: 'warning' },
};

export const SELO_DA_NEGOCIACAO: Record<SituacaoDaNegociacaoAssinaturas, Selo> = {
  EM_ANDAMENTO: { rotulo: 'Em andamento', variante: 'info' },
  COMPLETO: { rotulo: 'Completo', variante: 'success' },
  A_CONFERIR: { rotulo: 'A conferir', variante: 'warning' },
};

export const ROTULO_DO_ATALHO: Record<AtalhoDeSituacao, string> = {
  TODOS: 'Ver todos',
  NA_VEZ: 'Na vez',
  AGUARDANDO: 'Aguardando etapa anterior',
  A_CONFERIR: 'Informações a conferir',
};

export const SITUACOES_DO_FILTRO: readonly SituacaoDaParticipacao[] = [
  'ASSINOU',
  'NA_VEZ',
  'AGUARDANDO',
  'A_CONFERIR',
];

export const ROTULO_DO_TIPO: Record<TipoDaNegociacao, string> = {
  INDIVIDUAL: 'Individual',
  BULK: 'Em massa',
};

export const ROTULO_DO_FORMATO: Record<FormatoDoArquivo, string> = {
  csv: 'CSV',
  xlsx: 'Excel',
};

/**
 * Nome fixo da ação de clique no RUM para o botão de copiar: o `aria-label` traz o nome da pessoa
 * (para o leitor de tela), e sem este nome o RUM indexaria o `aria-label` como nome da ação.
 */
export const ACAO_DE_COPIAR = 'Copiar endereço de assinatura';

export const ROTULO_FALHA_NO_ENVIO = 'Falha no envio';
export const ROTULO_CPF_AUSENTE = 'CPF ausente';
const PESSOA_NAO_IDENTIFICADA = 'Pessoa não identificada';
const REPRESENTACAO_NAO_INFORMADA = 'Não informada';
const EM_NOME_PROPRIO = 'Em nome próprio';

/** Valores que o filtro de representação manda ao core no lugar de um nome de entidade. */
export const VALOR_NOME_PROPRIO = '__NOME_PROPRIO__';
export const VALOR_NAO_INFORMADA = '__NAO_INFORMADA__';

export function nomeDaPessoa(nome: string | null): string {
  return nome ?? PESSOA_NAO_IDENTIFICADA;
}

export function rotuloDaRepresentacao(representacao: RepresentacaoDeclarada | null): string {
  if (!representacao) return REPRESENTACAO_NAO_INFORMADA;
  return representacao.tipo === 'NOME_PROPRIO' ? EM_NOME_PROPRIO : representacao.entidadeNome;
}

export function rotuloDoValorDeRepresentacao(valor: string): string {
  if (valor === VALOR_NOME_PROPRIO) return EM_NOME_PROPRIO;
  if (valor === VALOR_NAO_INFORMADA) return REPRESENTACAO_NAO_INFORMADA;
  return valor;
}

/**
 * Texto de apoio do selo. Link enviado e Link aberto só existem na ZapSign; a falha no envio é o
 * bounce dela. A entrega nunca é deduzida da ausência de abertura.
 */
export function rotuloDoDetalhe(detalhe: DetalheDaParticipacao, depoisDe: string | null): string {
  switch (detalhe) {
    case 'ENVIADO':
      return 'Link enviado';
    case 'LINK_ABERTO':
      return 'Link aberto';
    case 'FALHA_ENVIO':
      return 'Link não entregue';
    case 'ASSINOU':
      return 'Assinado';
    case 'AGUARDA_VEZ':
      return depoisDe ? `Depois de ${depoisDe}` : 'Assina em seguida';
  }
}

/**
 * O mesmo registro na dica de "Quem falta assinar" do detalhe, com os textos que a tela 14 usa no
 * lugar dos da lista: "Link aberto, sem assinatura" e, para a pessoa não identificada que espera a
 * vez, "Sem envio registrado". Fica aqui, ao lado de `rotuloDoDetalhe`, para a troca de um rótulo
 * chegar às duas telas.
 */
export function rotuloDoDetalheNaPendencia(
  detalhe: DetalheDaParticipacao,
  depoisDe: string | null,
  identificada: boolean,
): string {
  if (detalhe === 'LINK_ABERTO') return `${rotuloDoDetalhe(detalhe, depoisDe)}, sem assinatura`;
  if (!identificada && detalhe === 'AGUARDA_VEZ') return 'Sem envio registrado';
  return rotuloDoDetalhe(detalhe, depoisDe);
}
